import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Link } from "react-router-dom";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin) navigate("/admin/dashboard", { replace: true });
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Innlogging feilet");
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#1A1715] px-5">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 flex flex-col items-center gap-4">
          <Logo />
          <span className="text-sm uppercase tracking-[0.25em] text-[#A8A29E]">Administrasjon</span>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-3xl border border-white/10 bg-[#282421] p-8 shadow-2xl"
          data-testid="admin-login-form"
        >
          <div className="mb-6 flex items-center gap-2 text-[#D4AF37]">
            <Lock className="h-5 w-5" />
            <h1 className="text-lg font-semibold text-[#FDFBF7]">Logg inn</h1>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-sm text-[#FDFBF7]" htmlFor="admin-email">
                E-post
              </label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="h-12 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-[#FDFBF7] outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-[#FDFBF7]" htmlFor="admin-password">
                Passord
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="h-12 w-full rounded-xl border border-white/10 bg-[#1A1715] px-4 text-[#FDFBF7] outline-none transition-colors focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40"
              />
            </div>

            {error && (
              <p className="text-sm text-red-400" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] font-700 uppercase tracking-wide text-[#1A1715] transition-colors hover:bg-[#E5C564] disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Logg inn"}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/gave"
            className="inline-flex items-center gap-1.5 text-sm text-[#A8A29E] hover:text-[#D4AF37]"
            data-testid="admin-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Tilbake til nettsiden
          </Link>
        </div>
      </div>
    </div>
  );
}
